import React, { useEffect, useMemo, useState } from "react";
import { Card, Table, Badge, Col, Button, Modal, Form } from "react-bootstrap";
import PageTitle from "../layouts/PageTitle";
import Spinner from "../common/Spinner";
import SwitchButton from "../components/ReactSwitch";
import { notifyError, notifyTopRight } from "../common/Toaster";
import {
  listCompanies,
  createCompany,
  updateCompany,
  searchCompanies,
  listCases,
} from "../../services/OcrTestSuite/OcrTestSuiteService";
import { getTexData } from "../../services/Tax/TaxService";

// OCR Test Suite — Test Companies screen (PLAN-ocr-test-suite.md §2, §7, Stage 3–4).
// One document per seed company (not per region): CA-BC and UK each have a
// tax-registered and a not-registered company. Tax profile is read-only, sourced
// live from the existing Tax Management data (admin/taxList); companies whose
// taxLookup is null show "Not registered".

// Format a TaxModel row into a one-line profile string.
const taxProfileLine = (taxRow) => {
  if (!taxRow) return "—";
  const parts = [];
  if (taxRow.taxName) parts.push(`${taxRow.taxName} ${taxRow.rate ?? 0}%`);
  if (taxRow.regionalTaxName)
    parts.push(`${taxRow.regionalTaxName} ${taxRow.regionalTax ?? 0}%`);
  return parts.length ? parts.join(" + ") : "—";
};

export default function OcrTestCompanies() {
  const [loader, setLoader] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [cases, setCases] = useState([]);
  const [taxRows, setTaxRows] = useState([]);

  // new/edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = new
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  // typeahead (new company)
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const fetchAll = async () => {
    setLoader(true);
    try {
      const [companiesRes, casesRes, taxRes] = await Promise.all([
        listCompanies(),
        listCases(),
        // large page — the tax table is small (one row per jurisdiction)
        getTexData(0, 500, "", ""),
      ]);
      setCompanies(companiesRes.data.data || []);
      setCases(casesRes.data.data || []);
      setTaxRows(taxRes.data.data || []);
    } catch (error) {
      notifyError(
        error?.response?.data?.message ||
          "Failed to load test companies (is ENABLE_OCR_TEST_SUITE set?)"
      );
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // fixture count per company
  const fixtureCountByCompany = useMemo(() => {
    const m = {};
    for (const tc of cases) {
      const k = String(tc.ocrTestCompanyId);
      m[k] = (m[k] || 0) + 1;
    }
    return m;
  }, [cases]);

  // match a company's taxLookup { country, province } to a TaxModel row
  const taxRowFor = (company) => {
    if (!company.taxLookup) return null;
    const { country, province } = company.taxLookup;
    return (
      taxRows.find(
        (t) =>
          (t.country || "").toLowerCase() === (country || "").toLowerCase() &&
          (t.province || "").toLowerCase() === (province || "").toLowerCase()
      ) || null
    );
  };

  // ── typeahead debounce ─────────────────────────────────────────────────
  useEffect(() => {
    if (!modalOpen || editing) return;
    const term = searchTerm.trim();
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await searchCompanies(term);
        setSearchResults(res.data.data || []);
      } catch (err) {
        console.log("searchCompanies error", err);
      } finally {
        setSearchLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [searchTerm, modalOpen, editing]);

  // ── modal ──────────────────────────────────────────────────────────────
  const openNew = () => {
    setEditing(null);
    setForm({
      regionCode: "",
      label: "",
      companyId: "",
      companyName: "",
      taxCountry: "",
      taxProvince: "",
      notRegistered: false,
    });
    setSearchTerm("");
    setSearchResults([]);
    setModalOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      regionCode: c.regionCode || "",
      label: c.label || "",
      companyId: String(c.companyId || ""),
      companyName: "",
      taxCountry: c.taxLookup?.country || "",
      taxProvince: c.taxLookup?.province || "",
      notRegistered: !c.taxLookup,
      isActive: c.isActive !== false,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setForm(null);
    setEditing(null);
  };

  const setFormField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickSearchResult = (u) => {
    setForm((f) => ({
      ...f,
      companyId: String(u._id),
      companyName: u.name,
      taxCountry: f.taxCountry || u.country || "",
      taxProvince: f.taxProvince || u.region || "",
    }));
    setSearchResults([]);
    setSearchTerm(u.name);
  };

  const saveCompany = async () => {
    if (!form.regionCode.trim()) {
      notifyError("Region code is required");
      return;
    }
    if (!editing && !form.companyId) {
      notifyError("Select a company");
      return;
    }
    setSaving(true);
    try {
      const taxLookup = form.notRegistered
        ? null
        : { country: form.taxCountry || null, province: form.taxProvince || null };
      if (editing) {
        await updateCompany(editing._id, {
          regionCode: form.regionCode.trim(),
          label: form.label.trim() || null,
          taxLookup,
          isActive: form.isActive,
        });
        notifyTopRight("Test company updated");
      } else {
        await createCompany({
          regionCode: form.regionCode.trim(),
          label: form.label.trim() || null,
          companyId: form.companyId,
          taxLookup,
        });
        notifyTopRight("Test company created");
      }
      setModalOpen(false);
      setForm(null);
      setEditing(null);
      await fetchAll();
    } catch (err) {
      notifyError(
        err?.response?.data?.message || err?.message || "Failed to save company"
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await updateCompany(c._id, { isActive: !(c.isActive !== false) });
      await fetchAll();
    } catch (err) {
      notifyError(
        err?.response?.data?.message || "Failed to toggle company active state"
      );
    }
  };

  // ── render ─────────────────────────────────────────────────────────────
  return (
    <div>
      <PageTitle activeMenu="Test Companies" motherMenu="OCR Test Suite" />
      <Col>
        <div className="d-flex justify-content-end mb-3">
          <Button variant="outline-primary" size="sm" onClick={openNew}>
            + New Test Company
          </Button>
        </div>

        <Card>
          <Card.Body>
            {loader ? (
              <Spinner />
            ) : (
              <Table responsive>
                <thead className="text-black">
                  <tr>
                    <th>
                      <strong>COMPANY</strong>
                    </th>
                    <th>
                      <strong>COMPANY ID</strong>
                    </th>
                    <th>
                      <strong>COUNTRY</strong>
                    </th>
                    <th>
                      <strong>REGION</strong>
                    </th>
                    <th>
                      <strong>TAX PROFILE</strong>
                    </th>
                    <th>
                      <strong>FIXTURES</strong>
                    </th>
                    <th>
                      <strong>ACTIVE</strong>
                    </th>
                    <th>
                      <strong>ACTIONS</strong>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((c) => {
                    const taxRow = taxRowFor(c);
                    return (
                      <tr key={c._id}>
                        <td>{c.label || "—"}</td>
                        <td
                          style={{ fontFamily: "monospace", fontSize: 12 }}
                        >
                          {String(c.companyId || "—")}
                        </td>
                        <td>
                          {c.taxLookup?.country || taxRow?.country || "—"}
                        </td>
                        <td>
                          {c.regionCode ? (
                            <Badge variant="info light">{c.regionCode}</Badge>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {c.taxLookup
                            ? taxProfileLine(taxRow)
                            : "Not registered"}
                        </td>
                        <td>{fixtureCountByCompany[String(c._id)] || 0}</td>
                        <td>
                          <SwitchButton
                            initialValue={c.isActive !== false}
                            onChange={() => toggleActive(c)}
                          />
                        </td>
                        <td>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => openEdit(c)}
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
            {companies.length === 0 && !loader && (
              <div className="justify-content-center d-flex my-5">
                No test companies seeded.
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      {/* New / Edit Test Company modal */}
      <Modal show={modalOpen} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editing ? "Edit Test Company" : "New Test Company"}
          </Modal.Title>
        </Modal.Header>
        {form && (
          <Modal.Body>
            <Form>
              {!editing && (
                <Form.Group>
                  <Form.Label>Company *</Form.Label>
                  {form.companyId ? (
                    <div className="d-flex align-items-center justify-content-between border rounded p-2">
                      <span>
                        {form.companyName || form.companyId}{" "}
                        <span
                          className="text-muted"
                          style={{ fontFamily: "monospace", fontSize: 11 }}
                        >
                          {form.companyId}
                        </span>
                      </span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            companyId: "",
                            companyName: "",
                          }))
                        }
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Form.Control
                        placeholder="Search a real company by name…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchLoading && (
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          Searching…
                        </div>
                      )}
                      {searchResults.length > 0 && (
                        <div
                          className="border rounded mt-1"
                          style={{ maxHeight: 180, overflow: "auto" }}
                        >
                          {searchResults.map((u) => (
                            <div
                              key={u._id}
                              className="p-2"
                              style={{ cursor: "pointer" }}
                              onClick={() => pickSearchResult(u)}
                            >
                              <div>{u.name}</div>
                              <div
                                className="text-muted"
                                style={{ fontSize: 11 }}
                              >
                                {[u.country, u.region]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </Form.Group>
              )}

              <div className="row">
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Region code *</Form.Label>
                    <Form.Control
                      value={form.regionCode}
                      onChange={(e) =>
                        setFormField("regionCode", e.target.value)
                      }
                      placeholder="e.g. CA-BC"
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Label</Form.Label>
                    <Form.Control
                      value={form.label}
                      onChange={(e) => setFormField("label", e.target.value)}
                      placeholder="e.g. BC — GST/PST Registered"
                    />
                  </Form.Group>
                </div>
              </div>

              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label="Not tax-registered (no tax profile)"
                  checked={form.notRegistered}
                  onChange={(e) =>
                    setFormField("notRegistered", e.target.checked)
                  }
                />
              </Form.Group>

              {!form.notRegistered && (
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Tax lookup — country</Form.Label>
                      <Form.Control
                        value={form.taxCountry}
                        onChange={(e) =>
                          setFormField("taxCountry", e.target.value)
                        }
                        placeholder="e.g. Canada"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Tax lookup — province</Form.Label>
                      <Form.Control
                        value={form.taxProvince}
                        onChange={(e) =>
                          setFormField("taxProvince", e.target.value)
                        }
                        placeholder="e.g. British Columbia"
                      />
                    </Form.Group>
                  </div>
                </div>
              )}

              {editing && (
                <Form.Group>
                  <Form.Label className="d-block">Active</Form.Label>
                  <SwitchButton
                    initialValue={form.isActive}
                    onChange={(v) => setFormField("isActive", v)}
                  />
                </Form.Group>
              )}
            </Form>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={closeModal}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={saveCompany} disabled={saving}>
            {saving ? "Saving…" : editing ? "Save changes" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
